import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Mail,
  Settings,
  Clock,
  Loader2,
  RefreshCw,
  FileText,
  AlertCircle,
  Inbox,
  ExternalLink,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailIngestionPanel() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();
      
      // Fetch config
      const configRes = await axios.get(`${API_URL}/api/email-ingestion/config`, { headers });
      setConfig(configRes.data);
      
      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/email-ingestion/stats`, { headers });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching email ingestion data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleIngestion = async (enabled) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/email-ingestion/toggle?enabled=${enabled}`, {}, { headers });
      setConfig(prev => ({ ...prev, enabled }));
      toast.success(`Email ingestion ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to toggle ingestion');
    }
  };

  const triggerCheck = async () => {
    setChecking(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/email-ingestion/check`, {}, { headers });
      
      if (response.data.status === 'success') {
        const results = response.data.results;
        toast.success(`Checked ${results.checked} emails, processed ${results.processed}, created ${results.documents_created} documents`);
        await fetchData();
      } else {
        toast.error(response.data.message || 'Check failed');
      }
    } catch (error) {
      toast.error('Failed to check emails');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-200 dark:bg-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Status</p>
              <Badge className={config?.enabled ? 'bg-green-500' : 'bg-slate-500'}>
                {config?.enabled ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <Activity className="w-6 h-6 text-slate-400" />
          </div>
        </div>
        <div className="bg-slate-200 dark:bg-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Ingested</p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{stats?.total_ingested || 0}</p>
            </div>
            <FileText className="w-6 h-6 text-emerald-500" />
          </div>
        </div>
        <div className="bg-slate-200 dark:bg-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Last 24 Hours</p>
              <p className="text-xl font-bold text-emerald-600">{stats?.last_24_hours || 0}</p>
            </div>
            <Clock className="w-6 h-6 text-blue-500" />
          </div>
        </div>
        <div className="bg-slate-200 dark:bg-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Whitelisted</p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{config?.allowed_senders?.length || 0}</p>
            </div>
            <Mail className="w-6 h-6 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6 border border-slate-300 dark:border-slate-600">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-emerald-500" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Quick Actions</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-200 dark:bg-slate-700 rounded-lg">
            <div>
              <Label className="text-slate-900 dark:text-slate-100">Enable Email Ingestion</Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">Automatically process incoming emails</p>
            </div>
            <Switch
              checked={config?.enabled || false}
              onCheckedChange={toggleIngestion}
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={triggerCheck} 
              disabled={checking || !config?.enabled}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Check Emails Now
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => navigate('/email-ingestion')}
              className="border-slate-400"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Full Settings
            </Button>
          </div>
        </div>
      </div>
      
      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800 dark:text-blue-200">Email-to-eTMF Ingestion</p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Configure subject patterns, sender whitelist, and default targets in the full settings page.
              Use patterns like <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">[STUDY:Name]</code> to automatically route documents.
            </p>
          </div>
        </div>
      </div>

      {/* Last Check Info */}
      {stats?.last_check && (
        <div className="text-center text-sm text-slate-500">
          Last checked: {new Date(stats.last_check).toLocaleString()}
        </div>
      )}
    </div>
  );
}
